import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Popover from "@mui/material/Popover";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../components/common/helpers/formatDate";
import {
  formatDateAsDdMmYyyy,
  formatDigitsAsDdMmYyyy,
  parseDdMmYyyyToDate,
} from "./dashboardDdMmYyyyInput";
import KpiOverview from "./KpiOverview";
import DashboardGraphSection from "./DashboardGraphSection";
import DashboardChartsArea from "./DashboardChartsArea";
import { DASHBOARD_GRAPH_OPTIONS } from "./dashboardGraphCatalog";
import DashboardDownloadMenu from "./DashboardDownloadMenu";
import { _Api } from "../common/api/_call";
import { mapKpiHeaderApiToDashboardData, mapKpiHeaderApiToModalData } from "./kpiDashboardModel";
import {
  DASHBOARD_CHART_GRAPH_IDS,
  DASHBOARD_CHART_LOAD_KEYS,
  mapCreditNoteGraphApiToModelPatch,
  mapDebitNoteGraphApiToModelPatch,
  mapDepositGraphApiToModelPatch,
  mapCustomerCategoryApiToModelPatch,
  mapCustomerGraphsApiResponse,
  mapPaymentMethodGraphApiToModelPatch,
  mapSaleOverviewApiToModelPatch,
  mapSalespersonGraphApiToModelPatch,
  mapSegmentGraphApiToModel,
  mapTotalSaleGraphApiToModelPatch,
  EMPTY_TOTAL_SALE_GROUPED_PATCH,
  mapWishlistGraphApiToModelPatch,
  mapCustomOrderGraphApiToModelPatch,
  mapRepairOrderGraphApiToModelPatch,
  mapExchangeGraphApiToModelPatch,
  mapRefundGraphApiToModelPatch,
  mapReservedGraphApiToModelPatch,
  mapCashRegisterGraphApiToModelPatch,
} from "./dashboardChartsModel";
import { buildTimestampedPdfFilename, buildTimestampedPngFilename, downloadElementAsPdf, downloadElementAsPng } from "./downloadCapturePng";
import "./PosDashboard.scss";

const createInitialChartLoading = () => ({
  [DASHBOARD_CHART_GRAPH_IDS.SALE_OVERVIEW]: true,
  [DASHBOARD_CHART_GRAPH_IDS.TOTAL_SALE]: true,
  [DASHBOARD_CHART_LOAD_KEYS.SEGMENT_GRAPH]: true,
  [DASHBOARD_CHART_GRAPH_IDS.WISHLIST]: true,
  [DASHBOARD_CHART_GRAPH_IDS.SALE_BY_CUSTOMER_CATEGORY]: true,
  [DASHBOARD_CHART_LOAD_KEYS.CUSTOMER_GRAPHS]: true,
  [DASHBOARD_CHART_GRAPH_IDS.SALESPERSON_PERFORMANCE]: true,
  [DASHBOARD_CHART_GRAPH_IDS.PAYMENT_METHOD]: true,
  [DASHBOARD_CHART_GRAPH_IDS.CREDIT_NOTE]: true,
  [DASHBOARD_CHART_GRAPH_IDS.DEBIT_NOTE]: true,
  [DASHBOARD_CHART_GRAPH_IDS.DEPOSIT]: true,
  [DASHBOARD_CHART_GRAPH_IDS.CUSTOM_ORDER]: true,
  [DASHBOARD_CHART_GRAPH_IDS.REPAIR_ORDER]: true,
  [DASHBOARD_CHART_GRAPH_IDS.EXCHANGE]: true,
  [DASHBOARD_CHART_GRAPH_IDS.REFUND]: true,
  [DASHBOARD_CHART_GRAPH_IDS.RESERVE]: true,
  [DASHBOARD_CHART_GRAPH_IDS.CASH_REGISTER]: true,
});

const normalizeStart = (d) =>
  d ? new Date(new Date(d).setHours(12, 10, 10, 999)) : null;
const normalizeEnd = (d) =>
  d ? new Date(new Date(d).setHours(23, 59, 59, 999)) : null;

const normalizeRangeUpdate = (update) => {
  if (!update || !Array.isArray(update)) return null;
  if (update[1] === null || update[1] === undefined) {
    const startOfDay = update[0] ? normalizeStart(update[0]) : null;
    return { start: startOfDay, end: null };
  }
  return {
    start: update[0] ? normalizeStart(update[0]) : null,
    end: update[1] ? normalizeEnd(update[1]) : null,
  };
};

/** Same calendar day (or both null). Avoids spurious setState when popover close re-normalizes Date instances. */
const sameCalendarDay = (a, b) => {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return moment(a).isSame(moment(b), "day");
};

const fieldCalendarIcon = (
  <span className="pos-dashboard__popover-field-icon" aria-hidden>
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M5.5 13.0601V13M9.4375 13.0601V13M9.4375 9.8V9.73984M12.9375 9.8V9.73984M2.875 6.59998H15.125M4.45833 2V3.20014M13.375 2V3.19999M13.375 3.19999H4.625C3.17525 3.19999 2 4.2745 2 5.59998V13.6C2 14.9255 3.17525 16 4.625 16H13.375C14.8247 16 16 14.9255 16 13.6L16 5.59998C16 4.2745 14.8247 3.19999 13.375 3.19999Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const getThisWeekendRange = () => {
  const today = moment().startOf("day");
  const dow = today.day();
  let sat;
  let sun;
  if (dow === 0) {
    sat = today.clone().subtract(1, "day");
    sun = today.clone();
  } else if (dow === 6) {
    sat = today.clone();
    sun = today.clone().add(1, "day");
  } else {
    sat = today.clone().day(6);
    sun = sat.clone().add(1, "day");
  }
  return {
    start: normalizeStart(sat.toDate()),
    end: normalizeEnd(sun.toDate()),
  };
};

const getNextWeekendRange = () => {
  const { start } = getThisWeekendRange();
  const nextSat = moment(start).add(7, "days").startOf("day");
  const nextSun = nextSat.clone().add(1, "day");
  return {
    start: normalizeStart(nextSat.toDate()),
    end: normalizeEnd(nextSun.toDate()),
  };
};

const DashboardSkeletonLine = ({ className = "" }) => (
  <span className={`pos-dashboard-skeleton-line ${className}`.trim()} aria-hidden />
);

const KpiOverviewSkeleton = () => (
  <section className="pos-dashboard-kpi-skeleton" aria-hidden>
    {Array.from({ length: 4 }).map((_, i) => (
      <article key={`kpi-skeleton-${i}`} className="pos-dashboard-kpi-skeleton__card">
        <DashboardSkeletonLine className="pos-dashboard-kpi-skeleton__title" />
        <DashboardSkeletonLine className="pos-dashboard-kpi-skeleton__value" />
        <DashboardSkeletonLine className="pos-dashboard-kpi-skeleton__meta" />
        <DashboardSkeletonLine className="pos-dashboard-kpi-skeleton__row" />
        <DashboardSkeletonLine className="pos-dashboard-kpi-skeleton__row" />
      </article>
    ))}
  </section>
);

const PosDashboard = () => {
  const { t } = useTranslation();
  const rangeAnchorRef = useRef(null);
  const dashboardCaptureRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const defaultStart = useMemo(
    () => moment().subtract(29, "days").startOf("day").toDate(),
    []
  );
  const defaultEnd = useMemo(() => moment().endOf("day").toDate(), []);
  const [visibleGraphIds, setVisibleGraphIds] = useState(
    () => new Set(DASHBOARD_GRAPH_OPTIONS.map((opt) => opt.id)),
  );
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [startInputText, setStartInputText] = useState("");
  const [endInputText, setEndInputText] = useState("");
  const [kpiHeaderLoading, setKpiHeaderLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(createInitialChartLoading);
  const [kpiData, setKpiData] = useState(null);
  const [kpiModalData, setKpiModalData] = useState(null);
  const [saleOverviewPartial, setSaleOverviewPartial] = useState(null);
  const [totalSalePartial, setTotalSalePartial] = useState(null);
  const [segmentGraphPartial, setSegmentGraphPartial] = useState(null);
  const [wishlistPartial, setWishlistPartial] = useState(null);
  const [saleByCustomerCategoryPartial, setSaleByCustomerCategoryPartial] = useState(null);
  const [customerTrendPartial, setCustomerTrendPartial] = useState(null);
  const [customerGroupPartial, setCustomerGroupPartial] = useState(null);
  const [ageAndGenderPartial, setAgeAndGenderPartial] = useState(null);
  const [salespersonPerformancePartial, setSalespersonPerformancePartial] = useState(null);
  const [paymentMethodPartial, setPaymentMethodPartial] = useState(null);
  const [creditNotePartial, setCreditNotePartial] = useState(null);
  const [debitNotePartial, setDebitNotePartial] = useState(null);
  const [depositPartial, setDepositPartial] = useState(null);
  const [customOrderPartial, setCustomOrderPartial] = useState(null);
  const [repairOrderPartial, setRepairOrderPartial] = useState(null);
  const [exchangePartial, setExchangePartial] = useState(null);
  const [refundPartial, setRefundPartial] = useState(null);
  const [reservedPartial, setReservedPartial] = useState(null);
  const [cashRegisterPartial, setCashRegisterPartial] = useState(null);

  const dayCount = useMemo(() => {
    if (!startDate || !endDate) return 0;
    return (
      moment(endDate).startOf("day").diff(moment(startDate).startOf("day"), "days") +
      1
    );
  }, [startDate, endDate]);

  const open = Boolean(anchorEl);

  useEffect(() => {
    setChartLoading(createInitialChartLoading());
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    const key = DASHBOARD_CHART_GRAPH_IDS.SALE_OVERVIEW;
    (async () => {
      try {
        if (!startDate) {
          if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
          return;
        }
        const payload = {
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate ?? startDate).format("YYYY-MM-DD"),
        };
        const res = await _Api(payload, "api/v1/POS/dashboard/sale-overview-graph");
        if (cancelled) return;
        if (res?.code === 200) {
          setSaleOverviewPartial(mapSaleOverviewApiToModelPatch(res));
        }
      } finally {
        if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    const key = DASHBOARD_CHART_GRAPH_IDS.TOTAL_SALE;
    (async () => {
      try {
        if (!startDate) {
          if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
          return;
        }
        const payload = {
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate ?? startDate).format("YYYY-MM-DD"),
        };
        const res = await _Api(payload, "api/v1/POS/dashboard/total-sale-graph");
        if (cancelled) return;
        if (res?.code === 200) {
          setTotalSalePartial(mapTotalSaleGraphApiToModelPatch(res));
        } else {
          setTotalSalePartial(EMPTY_TOTAL_SALE_GROUPED_PATCH);
        }
      } catch {
        if (!cancelled) setTotalSalePartial(EMPTY_TOTAL_SALE_GROUPED_PATCH);
      } finally {
        if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    const key = DASHBOARD_CHART_LOAD_KEYS.SEGMENT_GRAPH;
    (async () => {
      try {
        if (!startDate) {
          if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
          return;
        }
        const payload = {
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate ?? startDate).format("YYYY-MM-DD"),
        };
        const res = await _Api(payload, "api/v1/POS/dashboard/segment-graph");
        if (cancelled) return;
        if (res?.code === 200) {
          setSegmentGraphPartial(mapSegmentGraphApiToModel(res));
        }
      } finally {
        if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    const key = DASHBOARD_CHART_GRAPH_IDS.WISHLIST;
    (async () => {
      try {
        if (!startDate) {
          if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
          return;
        }
        const payload = {
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate ?? startDate).format("YYYY-MM-DD"),
        };
        const res = await _Api(payload, "api/v1/POS/dashboard/wishlist-graph");
        if (cancelled) return;
        if (res?.code === 200) {
          setWishlistPartial(mapWishlistGraphApiToModelPatch(res));
        }
      } finally {
        if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    const key = DASHBOARD_CHART_GRAPH_IDS.SALE_BY_CUSTOMER_CATEGORY;
    (async () => {
      try {
        if (!startDate) {
          if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
          return;
        }
        const payload = {
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate ?? startDate).format("YYYY-MM-DD"),
        };
        const res = await _Api(payload, "api/v1/POS/dashboard/customer-category");
        if (cancelled) return;
        if (res?.code === 200) {
          setSaleByCustomerCategoryPartial(mapCustomerCategoryApiToModelPatch(res));
        }
      } finally {
        if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    const key = DASHBOARD_CHART_LOAD_KEYS.CUSTOMER_GRAPHS;
    (async () => {
      try {
        if (!startDate) {
          if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
          return;
        }
        const payload = {
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate ?? startDate).format("YYYY-MM-DD"),
        };
        const res = await _Api(payload, "api/v1/POS/dashboard/customer-graphs");
        if (cancelled) return;
        if (res?.code === 200) {
          const next = mapCustomerGraphsApiResponse(res);
          setCustomerTrendPartial(next.customerTrendPartial);
          setCustomerGroupPartial(next.customerGroupPartial);
          setAgeAndGenderPartial(next.ageAndGenderPartial);
        }
      } finally {
        if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    const key = DASHBOARD_CHART_GRAPH_IDS.SALESPERSON_PERFORMANCE;
    (async () => {
      try {
        if (!startDate) {
          if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
          return;
        }
        const payload = {
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate ?? startDate).format("YYYY-MM-DD"),
        };
        const res = await _Api(payload, "api/v1/POS/dashboard/salesperson-graph");
        if (cancelled) return;
        if (res?.code === 200) {
          setSalespersonPerformancePartial(mapSalespersonGraphApiToModelPatch(res));
        }
      } finally {
        if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    const key = DASHBOARD_CHART_GRAPH_IDS.PAYMENT_METHOD;
    (async () => {
      try {
        if (!startDate) {
          if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
          return;
        }
        const payload = {
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate ?? startDate).format("YYYY-MM-DD"),
        };
        const res = await _Api(payload, "api/v1/POS/dashboard/payment-method-graph");
        if (cancelled) return;
        if (res?.code === 200) {
          setPaymentMethodPartial(mapPaymentMethodGraphApiToModelPatch(res));
        }
      } finally {
        if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    const key = DASHBOARD_CHART_GRAPH_IDS.CREDIT_NOTE;
    (async () => {
      try {
        if (!startDate) {
          if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
          return;
        }
        const payload = {
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate ?? startDate).format("YYYY-MM-DD"),
        };
        const res = await _Api(payload, "api/v1/POS/dashboard/credit-note-graph");
        if (cancelled) return;
        if (res?.code === 200) {
          setCreditNotePartial(mapCreditNoteGraphApiToModelPatch(res));
        }
      } finally {
        if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    const key = DASHBOARD_CHART_GRAPH_IDS.DEBIT_NOTE;
    (async () => {
      try {
        if (!startDate) {
          if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
          return;
        }
        const payload = {
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate ?? startDate).format("YYYY-MM-DD"),
        };
        const res = await _Api(payload, "api/v1/POS/dashboard/debit-note-graph");
        if (cancelled) return;
        if (res?.code === 200) {
          setDebitNotePartial(mapDebitNoteGraphApiToModelPatch(res));
        }
      } finally {
        if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    const key = DASHBOARD_CHART_GRAPH_IDS.DEPOSIT;
    (async () => {
      try {
        if (!startDate) {
          if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
          return;
        }
        const payload = {
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate ?? startDate).format("YYYY-MM-DD"),
        };
        const res = await _Api(payload, "api/v1/POS/dashboard/deposit-graph");
        if (cancelled) return;
        if (res?.code === 200) {
          setDepositPartial(mapDepositGraphApiToModelPatch(res));
        }
      } finally {
        if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    const key = DASHBOARD_CHART_GRAPH_IDS.CUSTOM_ORDER;
    (async () => {
      try {
        if (!startDate) {
          if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
          return;
        }
        const payload = {
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate ?? startDate).format("YYYY-MM-DD"),
        };
        const res = await _Api(payload, "api/v1/POS/dashboard/custom-order-graph");
        if (cancelled) return;
        if (res?.code === 200) {
          setCustomOrderPartial(mapCustomOrderGraphApiToModelPatch(res));
        }
      } finally {
        if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    const key = DASHBOARD_CHART_GRAPH_IDS.REPAIR_ORDER;
    (async () => {
      try {
        if (!startDate) {
          if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
          return;
        }
        const payload = {
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate ?? startDate).format("YYYY-MM-DD"),
        };
        const res = await _Api(payload, "api/v1/POS/dashboard/repair-order-graph");
        if (cancelled) return;
        if (res?.code === 200) {
          setRepairOrderPartial(mapRepairOrderGraphApiToModelPatch(res));
        }
      } finally {
        if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    const key = DASHBOARD_CHART_GRAPH_IDS.EXCHANGE;
    (async () => {
      try {
        if (!startDate) {
          if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
          return;
        }
        const payload = {
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate ?? startDate).format("YYYY-MM-DD"),
        };
        const res = await _Api(payload, "api/v1/POS/dashboard/exchange-graph");
        if (cancelled) return;
        if (res?.code === 200) {
          setExchangePartial(mapExchangeGraphApiToModelPatch(res));
        }
      } finally {
        if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    const key = DASHBOARD_CHART_GRAPH_IDS.REFUND;
    (async () => {
      try {
        if (!startDate) {
          if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
          return;
        }
        const payload = {
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate ?? startDate).format("YYYY-MM-DD"),
        };
        const res = await _Api(payload, "api/v1/POS/dashboard/refund-graph");
        if (cancelled) return;
        if (res?.code === 200) {
          setRefundPartial(mapRefundGraphApiToModelPatch(res));
        }
      } finally {
        if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    const key = DASHBOARD_CHART_GRAPH_IDS.RESERVE;
    (async () => {
      try {
        if (!startDate) {
          if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
          return;
        }
        const payload = {
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate ?? startDate).format("YYYY-MM-DD"),
        };
        const res = await _Api(payload, "api/v1/POS/dashboard/reserved-graph");
        if (cancelled) return;
        if (res?.code === 200) {
          setReservedPartial(mapReservedGraphApiToModelPatch(res));
        }
      } finally {
        if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    const key = DASHBOARD_CHART_GRAPH_IDS.CASH_REGISTER;
    (async () => {
      try {
        if (!startDate) {
          if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
          return;
        }
        const payload = {
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate ?? startDate).format("YYYY-MM-DD"),
        };
        const res = await _Api(payload, "api/v1/POS/dashboard/cash-register-graph");
        if (cancelled) return;
        if (res?.code === 200) {
          setCashRegisterPartial(mapCashRegisterGraphApiToModelPatch(res));
        }
      } finally {
        if (!cancelled) setChartLoading((s) => ({ ...s, [key]: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  useEffect(() => {
    let cancelled = false;
    setKpiHeaderLoading(true);
    (async () => {
      try {
        if (!startDate) {
          if (!cancelled) setKpiHeaderLoading(false);
          return;
        }
        const payload = {
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate ?? startDate).format("YYYY-MM-DD"),
        };
        const res = await _Api(payload, "api/v1/POS/dashboard/header");
        if (cancelled) return;
        if (res?.code === 200) {
          setKpiData(mapKpiHeaderApiToDashboardData(res));
          setKpiModalData(mapKpiHeaderApiToModalData(res));
        }
      } finally {
        if (!cancelled) setKpiHeaderLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  /** When the popover opens, sync typed fields from the current range. */
  useEffect(() => {
    if (!open) return;
    setStartInputText(formatDateAsDdMmYyyy(startDate));
    setEndInputText(formatDateAsDdMmYyyy(endDate));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync on open; dates may change from the calendar while open
  }, [open]);

  /** @param {Set<string> | ((prev: Set<string>) => Set<string>)} next — Set or updater (same pattern as React setState). */
  const handleGraphSelectionChange = useCallback((next) => {
    setVisibleGraphIds((prev) => {
      const resolved =
        typeof next === "function" ? next(prev instanceof Set ? prev : new Set(prev)) : next;
      return resolved instanceof Set ? new Set(resolved) : new Set();
    });
  }, []);

  const removeInlineDashboardGraph = useCallback(
    (graphId) => {
      if (!graphId) return;
      handleGraphSelectionChange((prev) => {
        const n = new Set(prev);
        n.delete(graphId);
        return n;
      });
    },
    [handleGraphSelectionChange],
  );

  const commitPopoverDateInputs = useCallback(() => {
    const startTrim = startInputText.trim();
    const endTrim = endInputText.trim();

    let nextStart = startDate;
    if (startTrim === "") {
      nextStart = null;
    } else {
      const ps = parseDdMmYyyyToDate(startTrim);
      if (!ps) {
        setStartInputText(formatDateAsDdMmYyyy(startDate));
        nextStart = startDate;
      } else {
        nextStart = normalizeStart(ps);
      }
    }

    let nextEnd = endDate;
    if (endTrim === "") {
      nextEnd = null;
    } else {
      const pe = parseDdMmYyyyToDate(endTrim);
      if (!pe) {
        setEndInputText(formatDateAsDdMmYyyy(endDate));
        nextEnd = endDate;
      } else {
        nextEnd = normalizeEnd(pe);
      }
    }

    if (nextStart && nextEnd && moment(nextStart).isAfter(moment(nextEnd), "day")) {
      nextEnd = normalizeEnd(moment(nextStart).toDate());
      setEndInputText(formatDateAsDdMmYyyy(nextEnd));
    }

    if (!sameCalendarDay(nextStart, startDate)) {
      setStartDate(nextStart);
    }
    if (!sameCalendarDay(nextEnd, endDate)) {
      setEndDate(nextEnd);
    }
  }, [startInputText, endInputText, startDate, endDate]);

  const openPicker = useCallback(() => {
    setAnchorEl(rangeAnchorRef.current);
  }, []);

  const onRangeChange = useCallback((update) => {
    const n = normalizeRangeUpdate(update);
    if (!n) return;
    if (n.end === null) {
      setStartDate(n.start);
      setEndDate(null);
      setStartInputText(n.start ? formatDateAsDdMmYyyy(n.start) : "");
      setEndInputText("");
      return;
    }
    setStartDate(n.start);
    setEndDate(n.end);
    setStartInputText(n.start ? formatDateAsDdMmYyyy(n.start) : "");
    setEndInputText(n.end ? formatDateAsDdMmYyyy(n.end) : "");
  }, []);

  const handleStartInputChange = useCallback((e) => {
    const next = formatDigitsAsDdMmYyyy(e.target.value);
    setStartInputText(next);
    const p = parseDdMmYyyyToDate(next);
    if (p) {
      const ns = normalizeStart(p);
      setStartDate(ns);
      setEndDate((prevEnd) => {
        if (prevEnd && moment(ns).isAfter(moment(prevEnd), "day")) {
          setEndInputText("");
          return null;
        }
        return prevEnd;
      });
    }
  }, []);

  const handleEndInputChange = useCallback((e) => {
    const next = formatDigitsAsDdMmYyyy(e.target.value);
    setEndInputText(next);
    const p = parseDdMmYyyyToDate(next);
    if (p) {
      const ne = normalizeEnd(p);
      setEndDate(ne);
      setStartDate((prevStart) => {
        if (prevStart && moment(ne).isBefore(moment(prevStart), "day")) {
          const day = moment(p).startOf("day").toDate();
          const ns = normalizeStart(day);
          setStartInputText(formatDateAsDdMmYyyy(day));
          return ns;
        }
        return prevStart;
      });
    }
  }, []);

  const applyQuickRange = useCallback((getRange) => {
    const { start, end } = getRange();
    setStartDate(start);
    setEndDate(end);
    setStartInputText(start ? formatDateAsDdMmYyyy(start) : "");
    setEndInputText(end ? formatDateAsDdMmYyyy(end) : "");
    setAnchorEl(null);
  }, []);

  const selectTodaySingleRange = useCallback(() => {
    const start = normalizeStart(moment().startOf("day").toDate());
    const end = normalizeEnd(moment().endOf("day").toDate());
    setStartDate(start);
    setEndDate(end);
    setStartInputText(formatDateAsDdMmYyyy(start));
    setEndInputText(formatDateAsDdMmYyyy(end));
  }, []);

  const quickRanges = useMemo(
    () => [
      {
        key: "today",
        label: t("Today", { defaultValue: "Today" }),
        getRange: () => ({
          start: normalizeStart(moment().startOf("day").toDate()),
          end: normalizeEnd(moment().endOf("day").toDate()),
        }),
      },
      {
        key: "yesterday",
        label: t("Yesterday", { defaultValue: "Yesterday" }),
        getRange: () => ({
          start: normalizeStart(moment().subtract(1, "day").startOf("day").toDate()),
          end: normalizeEnd(moment().subtract(1, "day").endOf("day").toDate()),
        }),
      },
      {
        key: "last7",
        label: t("Last 7 days", { defaultValue: "Last 7 days" }),
        getRange: () => ({
          start: normalizeStart(moment().subtract(6, "days").startOf("day").toDate()),
          end: normalizeEnd(moment().endOf("day").toDate()),
        }),
      },
      {
        key: "last14",
        label: t("Last 14 days", { defaultValue: "Last 14 days" }),
        getRange: () => ({
          start: normalizeStart(moment().subtract(13, "days").startOf("day").toDate()),
          end: normalizeEnd(moment().endOf("day").toDate()),
        }),
      },
      {
        key: "last4weeks",
        label: t("Last 4 weeks", { defaultValue: "Last 4 weeks" }),
        getRange: () => ({
          start: normalizeStart(moment().subtract(27, "days").startOf("day").toDate()),
          end: normalizeEnd(moment().endOf("day").toDate()),
        }),
      },
      {
        key: "thisWeekend",
        label: t("This weekend", { defaultValue: "This weekend" }),
        getRange: () => getThisWeekendRange(),
      },
      {
        key: "nextWeek",
        label: t("Next week", { defaultValue: "Next week" }),
        getRange: () => ({
          start: normalizeStart(moment().add(1, "week").startOf("isoWeek").toDate()),
          end: normalizeEnd(moment().add(1, "week").endOf("isoWeek").toDate()),
        }),
      },
      {
        key: "nextWeekend",
        label: t("Next weekend", { defaultValue: "Next weekend" }),
        getRange: () => getNextWeekendRange(),
      },
    ],
    [t]
  );

  const renderCalendarHeader = useCallback(
    ({
      monthDate,
      decreaseMonth,
      increaseMonth,
      changeMonth,
      changeYear,
      prevMonthButtonDisabled,
      nextMonthButtonDisabled,
    }) => {
      const cur = monthDate instanceof Date ? monthDate : new Date();
      const y = cur.getFullYear();
      const m = cur.getMonth();
      const years = [];
      for (let yr = y - 8; yr <= y + 8; yr += 1) {
        years.push(yr);
      }
      const months = moment.months();
      return (
        <div className="pos-dashboard-cal-custom-header">
          <button
            type="button"
            className="pos-dashboard-cal-nav"
            onClick={decreaseMonth}
            disabled={prevMonthButtonDisabled}
            aria-label={t("Previous month", { defaultValue: "Previous month" })}
          >
            ‹
          </button>
          <div className="pos-dashboard-cal-custom-header__dropdowns">
            <select
              className="pos-dashboard-cal-select"
              value={m}
              onChange={(e) => changeMonth(Number(e.target.value))}
              aria-label={t("Month", { defaultValue: "Month" })}
            >
              {months.map((name, i) => (
                <option key={name} value={i}>
                  {name}
                </option>
              ))}
            </select>
            <select
              className="pos-dashboard-cal-select"
              value={y}
              onChange={(e) => changeYear(Number(e.target.value))}
              aria-label={t("Year", { defaultValue: "Year" })}
            >
              {years.map((yr) => (
                <option key={yr} value={yr}>
                  {yr}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="pos-dashboard-cal-nav"
            onClick={increaseMonth}
            disabled={nextMonthButtonDisabled}
            aria-label={t("Next month", { defaultValue: "Next month" })}
          >
            ›
          </button>
          <button
            type="button"
            className="pos-dashboard-cal-today"
            onClick={selectTodaySingleRange}
          >
            {t("Today", { defaultValue: "Today" })}
          </button>
        </div>
      );
    },
    [t, selectTodaySingleRange]
  );

  const handleDashboardDownloadPdf = useCallback(async () => {
    await downloadElementAsPdf(dashboardCaptureRef.current, buildTimestampedPdfFilename("dashboard"));
  }, []);

  const handleDashboardDownloadPng = useCallback(async () => {
    await downloadElementAsPng(dashboardCaptureRef.current, buildTimestampedPngFilename("dashboard"));
  }, []);

  const dayHint =
    !startDate || !endDate
      ? `(0 ${t("Days", { defaultValue: "Days" })})`
      : dayCount === 1
        ? `(1 ${t("Day", { defaultValue: "Day" })})`
        : `(${dayCount} ${t("Days", { defaultValue: "Days" })})`;

  /** Keep the visible month aligned with typed dates (esp. end) so the teal range isn’t off-screen. */
  const calendarOpenToDate = useMemo(
    () => endDate ?? startDate ?? defaultStart,
    [endDate, startDate, defaultStart]
  );

  const datePlaceholder = t("datePlaceholder", { defaultValue: "DD/MM/YYYY" });

  const rangeLabel = useMemo(() => {
    if (!startDate && !endDate) {
      return `${datePlaceholder} — ${datePlaceholder}`;
    }
    if (startDate && !endDate) {
      return `${formatDate(startDate)} — ${datePlaceholder}`;
    }
    if (!startDate && endDate) {
      return `${datePlaceholder} — ${formatDate(endDate)}`;
    }
    return `${formatDate(startDate)} — ${formatDate(endDate)}`;
  }, [startDate, endDate, datePlaceholder]);

  const rangeLabelMuted = !startDate || !endDate;

  const unifiedRangeRow = () => (
    <div className="pos-dashboard__range-unified">
      <span
        className={
          rangeLabelMuted
            ? "pos-dashboard__range-unified-text pos-dashboard__range-unified-text--placeholder"
            : "pos-dashboard__range-unified-text"
        }
      >
        {rangeLabel}
      </span>
      <span className="pos-dashboard__range-unified-icon" aria-hidden>
        <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M5.5 13.0601V13M9.4375 13.0601V13M9.4375 9.8V9.73984M12.9375 9.8V9.73984M2.875 6.59998H15.125M4.45833 2V3.20014M13.375 2V3.19999M13.375 3.19999H4.625C3.17525 3.19999 2 4.2745 2 5.59998V13.6C2 14.9255 3.17525 16 4.625 16H13.375C14.8247 16 16 14.9255 16 13.6L16 5.59998C16 4.2745 14.8247 3.19999 13.375 3.19999Z"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </div>
  );

  const dateInputPlaceholder = t("datePlaceholder", { defaultValue: "DD/MM/YYYY" });

  const popoverDateInputs = () => (
    <div className="pos-dashboard__popover-range-row">
      <div className="pos-dashboard__popover-field">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          maxLength={10}
          value={startInputText}
          onChange={handleStartInputChange}
          onBlur={commitPopoverDateInputs}
          placeholder={dateInputPlaceholder}
          aria-label={t("Start date", { defaultValue: "Start date" })}
        />
        {fieldCalendarIcon}
      </div>
      <div className="pos-dashboard__popover-field">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          maxLength={10}
          value={endInputText}
          onChange={handleEndInputChange}
          onBlur={commitPopoverDateInputs}
          placeholder={dateInputPlaceholder}
          aria-label={t("End date", { defaultValue: "End date" })}
        />
        {fieldCalendarIcon}
      </div>
    </div>
  );

  return (
    <div className="pos-dashboard-page main-body">
      <div className="pos-dashboard" ref={dashboardCaptureRef}>
        <header className="pos-dashboard__header">
          <h1 className="pos-dashboard__title">
            {t("Dashboard", { defaultValue: "Dashboard" })}
          </h1>
          <div className="pos-dashboard__controls">
            <div
              ref={rangeAnchorRef}
              className="pos-dashboard__range-anchor"
              role="presentation"
            >
              <button
                type="button"
                className="pos-dashboard__range-hit"
                onClick={openPicker}
                aria-expanded={open}
                aria-haspopup="dialog"
                aria-label={t("Select date range", { defaultValue: "Select date range" })}
              >
                {unifiedRangeRow()}
              </button>
            </div>
            <span className="pos-dashboard__day-hint">{dayHint}</span>
          </div>
          <DashboardDownloadMenu
            buttonClassName="pos-dashboard__export"
            onDownloadPdf={handleDashboardDownloadPdf}
            onDownloadPng={handleDashboardDownloadPng}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 3v12m0 0l4-4m-4 4l-4-4M5 21h14"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </DashboardDownloadMenu>
        </header>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={() => {
            commitPopoverDateInputs();
            setAnchorEl(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          slotProps={{
            paper: {
              className: "pos-dashboard-popover-paper",
              elevation: 3,
            },
          }}
        >
          <div className="pos-dashboard-popover-stack">
            <div className="pos-dashboard-popover-fields">
              {popoverDateInputs()}
            </div>
            <div className="pos-dashboard-popover-body">
              <aside className="pos-dashboard-quick-dates" aria-label={t("Quick dates", { defaultValue: "Quick dates" })}>
                {quickRanges.map((q) => (
                  <button
                    key={q.key}
                    type="button"
                    className="pos-dashboard-quick-dates__btn"
                    onClick={() => applyQuickRange(q.getRange)}
                  >
                    {q.label}
                  </button>
                ))}
              </aside>
              <div className="pos-dashboard-calendar-wrap">
                <DatePicker
                  inline
                  selectsRange
                  monthsShown={1}
                  startDate={startDate}
                  endDate={endDate}
                  openToDate={calendarOpenToDate}
                  onChange={onRangeChange}
                  calendarStartDay={1}
                  renderCustomHeader={renderCalendarHeader}
                  calendarClassName="pos-dashboard-react-calendar"
                />
              </div>
            </div>
          </div>
        </Popover>

        <section className="pos-dashboard__content" aria-label={t("Overview", { defaultValue: "Overview" })}>
          {kpiHeaderLoading ? (
            <KpiOverviewSkeleton />
          ) : (
            <KpiOverview startDate={startDate} endDate={endDate} data={kpiData} modalData={kpiModalData} />
          )}
          <DashboardGraphSection selectedIds={visibleGraphIds} onSelectionChange={handleGraphSelectionChange} />
          <DashboardChartsArea
            visibleIds={visibleGraphIds}
            startDate={startDate}
            endDate={endDate}
            chartLoading={chartLoading}
            saleOverviewPartial={saleOverviewPartial}
            totalSalePartial={totalSalePartial}
            segmentGraphPartial={segmentGraphPartial}
            wishlistPartial={wishlistPartial}
            saleByCustomerCategoryPartial={saleByCustomerCategoryPartial}
            customerTrendPartial={customerTrendPartial}
            customerGroupPartial={customerGroupPartial}
            ageAndGenderPartial={ageAndGenderPartial}
            salespersonPerformancePartial={salespersonPerformancePartial}
            paymentMethodPartial={paymentMethodPartial}
            creditNotePartial={creditNotePartial}
            debitNotePartial={debitNotePartial}
            depositPartial={depositPartial}
            customOrderPartial={customOrderPartial}
            repairOrderPartial={repairOrderPartial}
            exchangePartial={exchangePartial}
            refundPartial={refundPartial}
            reservedPartial={reservedPartial}
            cashRegisterPartial={cashRegisterPartial}
            onRemoveInlineGraph={removeInlineDashboardGraph}
          />
        </section>
      </div>
    </div>
  );
};

export default PosDashboard;
